import React, {FC} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import {InteractionTypesEnum} from '../../../../../../libs/shared/core/src/helpers/interaction/interactionTypes';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontMouseEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {CheckBox} from '../../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {OkCancelDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {useLegacyInteractionContext} from '../../../../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  makeGetAssignProps,
  renderTriageDialogBody,
  renderTriageDialogTitle,
  triageActionDialogConfirmRenderer,
  triageActionLearnMoreLink,
  TriageTeamConfirmationDialogInheritableProps,
} from '../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {DismissedAlertsEnum} from '../../../../../models/api/preferencesModel';
import {emojiMap} from '../../../../../services/emojis/emojis';
import {trackInteraction} from '../../../../../services/interactionTracking';
import {AppState} from '../../../../../store/appState';
import {makeGetConversationsInboxes} from '../../../../../store/data/conversations/conversationsSelectors';
import {updateDismissedAlertsAction} from '../../../../../store/data/preferences/preferencesActions';
import {getDismissedAlerts} from '../../../../../store/data/preferences/preferencesSelectors';
import {InlineEmoji} from '../../../../common/emoji/inlineEmoji';

/*
 * Props.
 */

interface TriageTeamConfirmationDialogContainerProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
}

interface TriageTeamConfirmationDialogProps
  extends TriageTeamConfirmationDialogContainerProps,
    TriageTeamConfirmationDialogInheritableProps {
  onConfirmClick: () => void;
  onRequestClose: () => void;
  onCheckboxChange: (arg: boolean) => void;
  hasDismissedTeamArchiveAlert: boolean;
}

/*
 * Styles.
 */

/*
 * Selectors.
 */

function getConversations(_state: AppState, props: TriageTeamConfirmationDialogContainerProps) {
  return props.conversations;
}

function getCellsContext(_state: AppState, props: TriageTeamConfirmationDialogContainerProps) {
  return props.cellsContext;
}

function makeGetHasDismissedTeamArchiveAlert() {
  return createSelector([getDismissedAlerts], (dismissedAlerts) =>
    dismissedAlerts.includes(DismissedAlertsEnum.TRIAGE_TEAM),
  );
}

/*
 * Component.
 */

const BoldWrapper = styled.strong``;

const onLearnMoreClick: (arg: FrontMouseEvent<HTMLAnchorElement>) => void = (e) => {
  e.preventDefault();
  openInBrowser(triageActionLearnMoreLink);
};

const StyledLink = styled.a`
  color: ${(p) => p.theme.interactiveText};
  display: inline-block;
  &:hover {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const trailingPart = (
  <StyledLink
    href={triageActionLearnMoreLink}
    onClick={onLearnMoreClick}
    target="_blank"
    rel="noopener noreferrer"
  >
    <Trans id="triage-team-confirmation-learnmore">Learn more</Trans>
  </StyledLink>
);

const trailingPartWithEmoji = (
  <>
    {' '}
    <InlineEmoji emoji={emojiMap.raised_hands} size={20} />
    &nbsp;{trailingPart}
  </>
);

const TriageTeamConfirmationDialog: FC<TriageTeamConfirmationDialogProps> = (props) => {
  const interactionContext = useLegacyInteractionContext();

  const onClickWrapper = (handler: () => void) => () => {
    if (props.hasDismissedTeamArchiveAlert) {
      trackInteraction(
        InteractionTypesEnum.CHECKBOX_CHECKED,
        'dismiss-team-archive-dialog',
        interactionContext.parentInteractionIds,
      );
    }

    handler();
  };

  // If we're acting on behalf of another teammate, we don't want to let the user dismiss the alert.
  const shouldHideCheckbox = props.assignProps.assignee && !props.assignProps.isUserAssignee;

  return (
    <OkCancelDialog
      title={renderTriageDialogTitle(props)}
      okButtonLabel={triageActionDialogConfirmRenderer[props.action]}
      onOkClick={onClickWrapper(props.onConfirmClick)}
      onRequestClose={onClickWrapper(props.onRequestClose)}
      interactionId="archive-team-dialog"
    >
      {renderTriageDialogBody(props, BoldWrapper, trailingPartWithEmoji)}
      {!shouldHideCheckbox && (
        <>
          <br />
          <br />
          <CheckBox onChange={props.onCheckboxChange} isChecked={props.hasDismissedTeamArchiveAlert}>
            <Trans id="triage-team-confirmation-donotshowagain">Don&apos;t show me this again</Trans>
          </CheckBox>
        </>
      )}
    </OkCancelDialog>
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TriageTeamConfirmationDialogContainerProps>()(() => ({
  inboxes: makeGetConversationsInboxes(getConversations),
  assignProps: makeGetAssignProps(getCellsContext, getConversations),
  hasDismissedTeamArchiveAlert: makeGetHasDismissedTeamArchiveAlert(),
}));

const mapDispatchToProps = {
  updateDismissedAlertsAction,
};

export const TriageTeamConfirmationDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const onCheckboxChange: () => void = () => {
      dispatchProps.updateDismissedAlertsAction(DismissedAlertsEnum.TRIAGE_TEAM);
    };

    return {
      ...stateProps,
      ...ownProps,
      onCheckboxChange,
    };
  },
)(TriageTeamConfirmationDialog);
